import { computed, ref } from 'vue'

import type { ChangePlanRequest } from '../types/manage-subscription.types'

/**
 * Single module-level pending-checkout stash for membership payment flows.
 *
 * One global slot (not per-group Maps) so leave / group-switch / capture clear
 * paths stay obvious and a third flow does not invent another pattern.
 *
 * Clear **intent** lives here — call sites use the semantic helpers below instead
 * of raw unconditional clears, so matching rules (order / group) stay in one place:
 *
 * - `completePendingChangePlanForOrder` — PUT / payment succeeded for this order
 * - `abandonPendingChangePlanForGroup` — fail, supersede, or cancel for that group
 * - `abandonPendingChangePlanUnlessGroup` — active group context moved away
 * - `clearAllPendingCheckoutState` — leave-group / fresh `/plans` visit
 *
 * `leaveJoinPlans` / `leaveMembershipPage` deliberately do **not** clear change-plan
 * pending so checkout can finish after Back.
 *
 * - `change-plan`: billing-type switch waiting on checkout orderId
 * - `join-offer`: legacy plan-selection scratch (capture no longer needs the
 *   offer id; kept for clear/test paths that still set it)
 */

/** Checkout-in-flight target for a billing-type change-plan switch. */
export type PendingChangePlan = Pick<ChangePlanRequest, 'tierId' | 'priceId'> & {
  groupId: string
  locationId: string
  /** Bound in onOrderCreated so a later payment cannot PUT a stale tier/price. */
  orderId?: string
}

export interface PendingJoinOffer {
  groupId: string
  offerId: string
  orderId?: string
}

export type PendingCheckout = ({ kind: 'change-plan' } & PendingChangePlan) | ({ kind: 'join-offer' } & PendingJoinOffer)

const pendingCheckout = ref<PendingCheckout | null>(null)

export const pendingCheckoutComputed = computed(() => pendingCheckout.value)

export const pendingChangePlanComputed = computed((): PendingChangePlan | null => {
  const pending = pendingCheckout.value
  if (pending?.kind !== 'change-plan') return null
  const { kind: _kind, ...rest } = pending
  return rest
})

/** Mirrored for callers that still read a reactive “current” join offer (tests / debug). */
export const pendingJoinOfferIdComputed = computed(() => {
  const pending = pendingCheckout.value
  return pending?.kind === 'join-offer' ? pending.offerId : ''
})

export const setPendingChangePlanState = (pending: PendingChangePlan): void => {
  pendingCheckout.value = { kind: 'change-plan', ...pending }
}

/**
 * Checkout / PUT finished — drop the matching change-plan stash.
 * Bound pending requires a matching `orderId`; unbound pending clears on any complete.
 */
export const completePendingChangePlanForOrder = (orderId?: string): void => {
  const pending = pendingCheckout.value
  if (pending?.kind !== 'change-plan') return
  if (pending.orderId && pending.orderId !== orderId) return
  pendingCheckout.value = null
}

/**
 * Abandon in-flight change-plan for this group (payment fail, first-join supersede,
 * checkout open error). No-op when pending belongs to a different group.
 */
export const abandonPendingChangePlanForGroup = (groupId: string): void => {
  const pending = pendingCheckout.value
  if (pending?.kind === 'change-plan' && pending.groupId === groupId) {
    pendingCheckout.value = null
  }
}

/**
 * Drop change-plan pending when navigating away from its group (or clearing current group).
 * Same-group updates keep an in-flight checkout pending intact.
 */
export const abandonPendingChangePlanUnlessGroup = (groupId: string | null | undefined): void => {
  const pending = pendingCheckout.value
  if (pending?.kind === 'change-plan' && pending.groupId !== groupId) {
    pendingCheckout.value = null
  }
}

/** Attach the checkout order so the payment watcher can bind PUT to this attempt. */
export const bindPendingChangePlanOrderId = (orderId: string): void => {
  const pending = pendingCheckout.value
  if (!pending || pending.kind !== 'change-plan' || !orderId) return
  pendingCheckout.value = { ...pending, orderId }
}

export const setPendingJoinOfferState = (groupId: string, offerId: string): void => {
  if (!groupId || !offerId) return
  pendingCheckout.value = { kind: 'join-offer', groupId, offerId }
}

export const bindPendingJoinOfferOrderId = (orderId: string, groupId: string): void => {
  const pending = pendingCheckout.value
  if (!orderId || !groupId || pending?.kind !== 'join-offer' || pending.groupId !== groupId) return
  pendingCheckout.value = { ...pending, orderId }
}

/**
 * Drops join-offer pending for one group, or every join-offer when omitted.
 * Never clears a change-plan slot — that has its own clear helpers so checkout
 * can keep an in-flight billing switch while join scratch is wiped.
 */
export const clearPendingJoinOfferState = (groupId?: string): void => {
  const pending = pendingCheckout.value
  if (!pending || pending.kind !== 'join-offer') return
  if (!groupId || pending.groupId === groupId) {
    pendingCheckout.value = null
  }
}

export const pendingJoinOfferFor = (groupId: string, orderId?: string): string => {
  const pending = pendingCheckout.value
  if (pending?.kind !== 'join-offer' || pending.groupId !== groupId) return ''
  if (orderId) {
    // Exact order bind required when the caller passes an orderId.
    return pending.orderId === orderId ? pending.offerId : ''
  }
  return pending.offerId
}

/** Clears any pending checkout (join or change-plan) — leave-group / fresh `/plans`. */
export const clearAllPendingCheckoutState = (): void => {
  pendingCheckout.value = null
}
